import { t, Trans } from '@lingui/macro'
import { Divider, Table, TableCell, TableContainer, TableRow } from '@mui/material'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import TableBody from '@mui/material/TableBody'
import TableHead from '@mui/material/TableHead'
import Typography from '@mui/material/Typography'
import { makeStyles } from '@mui/styles'
import { dateFormat } from 'app/appSettings'
import { MILESTONE_ORDER } from 'app/services/sfAuth/sfData/sfOpportunity'
import CustomDatePicker from 'app/views/common-components/date-pickers/CustomDatePicker'
import { countHelperText } from 'app/views/common-components/FormElement'
import { FormTopLabel } from 'app/views/common-components/forms/FormTopLabel'
import { TooltipLabelIcon } from 'app/views/common-components/TooltipLabelIcon'
import { TextFieldWithFormik } from 'app/views/common-components/with-formik/TextFieldWithFormik'
import { getCurrentWords } from 'app/views/common/Formats'
import { FormHelptext } from 'app/views/forms/common/FormHelpText'
import { requiredTrans } from 'app/views/forms/formTranslations'
import { commitChangeToMultipleFields } from 'app/views/forms/multiuser/grpcMultiuserEdit'
import {
  completeMinMaxDates,
  endDateChange,
  getMinMaxDates,
  startDateChange
} from 'app/views/utilities/milestone-date-calcs'
import { useFormikContext } from 'formik'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { languages, myI18n } from 'translation/I18nConnectedProvider'
import * as Yup from 'yup'
import { parseFormLabelText } from '../../../common/Common'
import MUDatePicker from '../../../multiuser/components/MUDatePicker'
import { disabledDatePickerHelperText, getMinMaxDates as calculateMinMaxDates, shouldDatePickerBeDisable } from '../../date-picker/FormDatePicker'
import { calendarStyles } from '../../date-picker/FormEditorDatePicker'

const PHASE_DURATION_DAYS = 7

const styles = (theme) => ({
  root: {
    margin: theme.spacing(2),
    padding: theme.spacing(2)
    // width: 800
  },
  dataPicker: {
    paddingLeft: 15,
    paddingRight: 15,
    paddingTop: 15,
    width: '80%',
    marginBottom: 16
  },
  tcSpan4: {
    paddingLeft: 50,
    paddingTop: 25,
    paddingBottom: 25,
    paddingRight: 35
  },
  tr: {
    borderBottom: 'solid 1px #c1c1c1',
    borderTop: 'solid 0px #ffffff'
  },
  tHeaderText: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center'
  },
  divCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
})

export const formMilestonesValueToText = (v, question) => {
  const { phases } = question.typeProps
  const toRet = (
    <div>
      {phases.filter((phase, index) => v[index]).map((phase, index) => {
        const milestone = v[index]
        return (
          <div key={index} style={{ width: '100%', padding: 5 }}>
            <Typography
              style={{
                fontSize: 14,
                fonWeight: 'bold',
                textAlign: 'center',
                margin: 10
              }}
            >
              <Trans id={myI18n?._(t`${phase.apiValue}`)} />
            </Typography>
            <Grid container style={{ marginBottom: 10 }}>
              <Grid item xs style={{ textAlign: 'center' }}>
                <Typography className='form-print-subtitle'>
                  <Trans>Start Date</Trans>
                </Typography>
                <Typography style={{ fontSize: 17 }}>
                  {milestone?.startDate &&
                    moment.utc(milestone.startDate).format(dateFormat)}
                </Typography>
              </Grid>
              <Grid item xs style={{ textAlign: 'center' }}>
                <Typography className='form-print-subtitle'>
                  <Trans>End Date</Trans>
                </Typography>
                <Typography style={{ fontSize: 17 }}>
                  {milestone?.endDate &&
                    moment.utc(milestone.endDate).format(dateFormat)}
                </Typography>
              </Grid>
            </Grid>
            <Divider />
            <div style={{ padding: 5 }}>
              <Typography className='form-print-subtitle'>
                <Trans>Primary Activities</Trans>
              </Typography>
              <Typography>{milestone.primaryActivities}</Typography>
            </div>
            <Divider />
            <div style={{ padding: 5 }}>
              <Typography className='form-print-subtitle'>
                <Trans>Comments</Trans>
              </Typography>
              <Typography>{milestone.comments}</Typography>
            </div>
          </div>
        )
      })}
      <div style={{ color: '#e0a912' }}>
        <Trans>
          Overriding will ony save changes to already added milestones. It will
          not delete any newly added milestones. Manual cleanup may be required.
        </Trans>
      </div>
    </div>
  )

  return languages.reduce((acc, lang) => {
    acc[lang] = toRet
    return acc
  }, {})
}

export const FormMilestonesDefaultValue = (obj, addInfo, item) => {
  const { phases = [] } = item.typeProps

  const allMilestoneDatesMissing =
    !obj ||
    !obj.FGM_Base__Benchmarks__r ||
    obj.FGM_Base__Benchmarks__r.records.every(
      m => !m.FGM_Base__Due_Date__c && !m.FGM_Base__Completion_Date__c
    )

  // calculate first possible days for all milestones
  const calculatedDefaultsMinDates = []
  if (allMilestoneDatesMissing) {
    phases.forEach((currentPhase, index) => {
      const startDateMinDate = currentPhase.startDateMinDate
      const startDateMaxDate = currentPhase.startDateMaxDate
      const startDateMinDateElement = currentPhase.startDateMinDateElement
      const startDateMaxDateElement = currentPhase.startDateMaxDateElement

      const endDateMinDate = currentPhase.endDateMinDate
      const endDateMaxDate = currentPhase.endDateMaxDate
      const startDateDaysAfterCurrentDate = currentPhase.startDateDaysAfterCurrentDate
      const endDateMinDateElement = currentPhase.endDateMinDateElement
      const endDateMaxDateElement = currentPhase.endDateMaxDateElement

      const {
        startDateMinDate: startMinDate,
        endDateMinDate: endMinDate
      } = getMilestoneDateLimits({
        startDateMinDate,
        startDateMaxDate,
        startDateDaysAfterCurrentDate,
        startDateMinDateElement,
        startDateMaxDateElement,
        endDateMinDate,
        endDateMaxDate,
        endDateMinDateElement,
        endDateMaxDateElement
      })

      if (index === 0) {
        const calculatedEndMinDate = moment.utc(startMinDate).add(PHASE_DURATION_DAYS, 'days')
        const finalEndMinDate = moment.max(endMinDate, calculatedEndMinDate)

        calculatedDefaultsMinDates.push({
          startMinDate,
          endMinDate: finalEndMinDate
        })
      } else {
        const previousEndMinDate = calculatedDefaultsMinDates[index - 1].endMinDate
        const formattedPreviousEndMinDate = previousEndMinDate.format(dateFormat)
        const formattedStartMinDate = startMinDate.format(dateFormat)

        if (formattedPreviousEndMinDate >= formattedStartMinDate) {
          const adjustedStartDate = moment.utc(previousEndMinDate).add(1, 'day')
          const adjustedEndDate = moment.utc(adjustedStartDate).add(PHASE_DURATION_DAYS, 'days')

          calculatedDefaultsMinDates.push({
            startMinDate: adjustedStartDate,
            endMinDate: adjustedEndDate
          })
        } else {
          const calculatedEndMinDate = moment.utc(startMinDate).add(7, 'days')
          const finalEndMinDate = moment.max(endMinDate, calculatedEndMinDate)

          calculatedDefaultsMinDates.push({
            startMinDate,
            endMinDate: finalEndMinDate
          })
        }
      }
    })
  }

  if (!obj || !obj.FGM_Base__Benchmarks__r) {
    return phases.map((phase, index) => ({
      startDate: calculatedDefaultsMinDates.at(index)
        ?.startMinDate.format(dateFormat),
      endDate: calculatedDefaultsMinDates.at(index)
        ?.endMinDate.format(dateFormat),
      isDisplayed: true,
      isActive: true
    }))
  }

  const toRet = obj.FGM_Base__Benchmarks__r.records
    .sort((a, b) => {
      const aIsActive = phases.some(
        (phase) => phase.apiValue === a.FGM_Base__Status__c
      )
      const bIsActive = phases.some(
        (phase) => phase.apiValue === b.FGM_Base__Status__c
      )
      if (aIsActive && !bIsActive) {
        return -1
      } else if (!aIsActive && bIsActive) {
        return 1
      } else {
        return (
          MILESTONE_ORDER.indexOf(a.stage) - MILESTONE_ORDER.indexOf(b.stage)
        )
      }
    })
    .map((milestone, index) => {
      const isDisplayed = index < phases.length
      let startDate = allMilestoneDatesMissing
        ? calculatedDefaultsMinDates.at(index)?.startMinDate
        : moment.utc(milestone.FGM_Base__Due_Date__c)
      let endDate = allMilestoneDatesMissing
        ? calculatedDefaultsMinDates.at(index)?.endMinDate
        : moment.utc(milestone.FGM_Base__Completion_Date__c)

      if (!startDate || !startDate.isValid()) {
        startDate = null
      } else {
        startDate = startDate.format(dateFormat)
      }
      if (!endDate || !endDate.isValid()) {
        endDate = null
      } else {
        endDate = endDate.format(dateFormat)
      }
      return {
        isActive: phases.some(
          (phase) => phase.apiValue === milestone.FGM_Base__Status__c
        ),
        isDisplayed,
        startDate,
        endDate,
        primaryActivities: milestone.Primary_activities__c || '',
        comments: milestone.FGM_Base__Description__c || '',
        stage: milestone.FGM_Base__Status__c,
        id: milestone.Id
      }
    })
  return toRet
}

export const getMinMaxDatesErrors = ({ date, minDate, maxDate, minError, maxError, path }) => {
  const errors = []

  const getFormattedDate = (date) => {
    return date
      ? moment.utc(date).format(dateFormat)
      : null
  }
  const formattedDate = getFormattedDate(date)
  const formattedMinDate = getFormattedDate(minDate)
  const formattedMaxDate = getFormattedDate(maxDate)

  if (
    formattedMinDate &&
    formattedMaxDate &&
    formattedMinDate > formattedMaxDate
  ) {
    errors.push(
      new Yup.ValidationError(
        (
          <>
            <Trans>FORM_DATE_PICKER_INVALID_MIN_MAX_ERROR</Trans>
            <div>{formattedMinDate}</div>
            <div>{formattedMaxDate}</div>
          </>
        ),
        formattedMinDate + formattedMaxDate,
        path
      )
    )
  }

  if (formattedMinDate && formattedDate < formattedMinDate) {
    errors.push(
      new Yup.ValidationError(
        minError,
        formattedMinDate,
        path
      )
    )
  }

  if (formattedMaxDate && formattedDate > formattedMaxDate) {
    errors.push(
      new Yup.ValidationError(
        (<>{maxError}</>),
        formattedMaxDate,
        path
      )
    )
  }
  return errors
}

const getMilestoneDateLimits = ({
  values,
  data,
  startDateMinDate,
  startDateMaxDate,
  startDateDaysAfterCurrentDate,
  startDateMinDateElement,
  startDateMaxDateElement,
  endDateMinDate,
  endDateMaxDate,
  endDateMinDateElement,
  endDateMaxDateElement
}) => {
  values = values ?? {}
  data = data ?? {}

  // calculate min and max dates for start date
  const {
    minMessage: startMinMessage,
    maxMessage: startMaxMessage,
    minDate: calculatedStartDateMinDate,
    maxDate: calculatedStartDateMaxDate,
    minErrorMessage: startMinError,
    maxErrorMessage: startMaxError
  } = calculateMinMaxDates({
    typeProps: {
      minDate: startDateMinDate, // values?.[id][index]?.startDateMinDate,
      maxDate: startDateMaxDate, // values?.[id][index]?.startDateMaxDate,
      startDateDaysAfterCurrentDate: startDateDaysAfterCurrentDate || 1,
      minDateElement: startDateMinDateElement,
      maxDateElement: startDateMaxDateElement
    },
    shouldUseOffsetMessages: false,
    values,
    data
  })

  const firstEndMinDate = getFirstPossibleEndDate({
    endDateMinDate, // values?.[id][index]?.endDateMinDate,
    startDateMinDate: calculatedStartDateMinDate
  })

  // calculate min and max dates for end date
  const {
    minMessage: endMinMessage,
    maxMessage: endMaxMessage,
    minDate: calculatedEndDateMinDate,
    maxDate: calculatedEndDateMaxDate,
    minErrorMessage: endMinError,
    maxErrorMessage: endMaxError
  } = calculateMinMaxDates({
    typeProps: {
      minDate: firstEndMinDate,
      maxDate: endDateMaxDate, // values?.[id][index]?.endDateMaxDate,
      minDateElement: endDateMinDateElement,
      maxDateElement: endDateMaxDateElement
    },
    shouldUseOffsetMessages: false,
    values,
    data
  })

  return {
    startMinMessage,
    startMaxMessage,
    startDateMinDate: calculatedStartDateMinDate,
    startDateMaxDate: calculatedStartDateMaxDate,
    endMinMessage,
    endMaxMessage,
    endDateMinDate: calculatedEndDateMinDate,
    endDateMaxDate: calculatedEndDateMaxDate,
    startMinError,
    startMaxError,
    endMinError,
    endMaxError
  }
}

Yup.addMethod(Yup.array, 'checkChronology', function (message) {
  return this.test('checkChronology', message, function (milestones) {
    const { path, createError } = this
    if (milestones.length === 0) {
      return true
    }
    const activeMilestones = milestones.filter((m) => m.isDisplayed)
    console.log({ activeMilestones, milestones })
    const startDates = activeMilestones.map((item) => item.startDate)
    const endDates = activeMilestones.map((item) => item.endDate)
    let bool = false
    const errors = []
    for (let index = 1; index < startDates.length; index++) {
      if (startDates[index] && startDates[index] <= endDates[index - 1]) {
        bool = true
        errors.push(
          new Yup.ValidationError(
            message,
            startDates[index],
            path + `[${index}].startDate`
          )
        )
        errors.push(
          new Yup.ValidationError(
            message,
            endDates[index],
            path + `[${index - 1}].endDate`
          )
        )
      }
    }
    for (let index = 0; index < endDates.length; index++) {
      if (endDates[index] <= startDates[index]) {
        bool = true
        errors.push(
          new Yup.ValidationError(
            message,
            endDates[index],
            path + `[${index}].endDate`
          )
        )
        errors.push(
          new Yup.ValidationError(
            message,
            startDates[index],
            path + `[${index}].startDate`
          )
        )
      }
    }
    if (bool) {
      return createError({ message: () => errors })
      // return createError({ path, message })
    }
    return !bool
  })
})

// If days quantity after current date is set, check if start date is not before this date
Yup.addMethod(Yup.array, 'checkStartDateMinDate', function ({ phases, data, formikRef }) {
  return this.test('checkStartDateMinDate', '', function (milestones) {
    const { path, createError } = this
    const values = formikRef?.current?.values

    if (milestones.length === 0) {
      return true
    }
    const activeMilestones = milestones.filter((m) => m.isDisplayed)

    const errors = []
    activeMilestones.forEach((item, index) => {
      // startDate and endDate can be either Date object or moment object or string in format 'YYYY-MM-DD'. Bring them all to format 'YYYY-MM-DD' for comparison
      const startDate = item?.startDate?.format // is moment object with format method
        ? item?.startDate?.format(dateFormat)
        : item?.startDate instanceof Date // is Date object
          ? item?.startDate?.toLocaleDateString('en-CA')
          : item?.startDate // is string in format dateFormat

      const endDate = item?.endDate?.format
        ? item?.endDate?.format(dateFormat)
        : item?.endDate instanceof Date
          ? item?.endDate?.toLocaleDateString('en-CA')
          : item?.endDate

      const currentPhase = phases[index]

      const startDateMinDate = currentPhase.startDateMinDate
      const startDateMaxDate = currentPhase.startDateMaxDate
      const startDateMinDateElement = currentPhase.startDateMinDateElement
      const startDateMaxDateElement = currentPhase.startDateMaxDateElement

      const endDateMinDate = currentPhase.endDateMinDate
      const endDateMaxDate = currentPhase.endDateMaxDate
      const startDateDaysAfterCurrentDate = currentPhase.startDateDaysAfterCurrentDate
      const endDateMinDateElement = currentPhase.endDateMinDateElement
      const endDateMaxDateElement = currentPhase.endDateMaxDateElement

      const {
        startDateMinDate: startMinDate,
        startDateMaxDate: startMaxDate,
        endDateMinDate: endMinDate,
        endDateMaxDate: endMaxDate,
        startMinError,
        startMaxError,
        endMinError,
        endMaxError
      } = getMilestoneDateLimits({
        values,
        data,
        startDateMinDate,
        startDateMaxDate,
        startDateDaysAfterCurrentDate,
        startDateMinDateElement,
        startDateMaxDateElement,
        endDateMinDate,
        endDateMaxDate,
        endDateMinDateElement,
        endDateMaxDateElement
      })

      const startErrors = getMinMaxDatesErrors({
        date: startDate,
        minDate: startMinDate,
        maxDate: startMaxDate,
        minError: startMinError,
        maxError: startMaxError,
        path: path + `[${index}].startDate`
      })

      const endErrors = getMinMaxDatesErrors({
        date: endDate,
        minDate: endMinDate,
        maxDate: endMaxDate,
        minError: endMinError,
        maxError: endMaxError,
        path: path + `[${index}].endDate`
      })
      errors.push(...startErrors)
      errors.push(...endErrors)
    })

    if (errors.length > 0) {
      return createError({ message: () => errors })
      // return createError({ path, message })
    }
    return true
  })
})

// Check if milestones have valid dates
Yup.addMethod(Yup.array, 'checkValidDates', function (message, isRequired) {
  return this.test('checkValidDates', message, function (milestones) {
    const { path, createError } = this
    if (milestones.length === 0) {
      return true
    }
    const activeMilestones = milestones.filter((m) => m.isDisplayed)
    let bool = false
    const errors = []
    for (let index = 0; index < activeMilestones.length; index++) {
      const startDate = activeMilestones[index].startDate
      if (!isRequired && !startDate) {
        continue
      }
      const startDateValidation = moment(startDate, dateFormat, true).isValid()
      if (!startDateValidation) {
        bool = true
        errors.push(
          new Yup.ValidationError(
            message,
            startDate,
            path + `[${index}].startDate`
          )
        )
      }
    }
    for (let index = 0; index < activeMilestones.length; index++) {
      const endDate = activeMilestones[index].endDate
      if (!isRequired && !endDate) {
        continue
      }
      const endDateValidation = moment(endDate, dateFormat, true).isValid()
      if (!endDateValidation) {
        bool = true
        errors.push(
          new Yup.ValidationError(
            message,
            endDate,
            path + `[${index}].endDate`
          )
        )
      }
    }
    if (bool) {
      return createError({ message: () => errors })
      // return createError({ path, message })
    }
    return !bool
  })
})

export const FormMilestonesValidation = (item, data, formikRef) => {
  const { commentsRequired, activitiesRequired, datesRequired } =
    item.typeProps
  const baseObject = {}
  const chronologicalTrans = (
    <Trans>Dates have to be in chronological order!</Trans>
  )
  const validDatesTrans = (
    <Trans>VALID_DATE_FORMAT_ERROR</Trans>
  )
  if (commentsRequired) {
    baseObject.comments = Yup.string()
      .ensure()
      .required(requiredTrans)
      .label(<Trans>Comments</Trans>)
  }
  if (activitiesRequired) {
    baseObject.primaryActivities = Yup.string()
      .ensure()
      .required(requiredTrans)
      .label(<Trans>Primary Activities</Trans>)
  }
  if (datesRequired) {
    baseObject.startDate = Yup.date()
      .typeError(<Trans>Must be valid date</Trans>)
      // .min(moment.utc(), notAllowedTrans)
      .required(requiredTrans)
      .label(<Trans>Start Date</Trans>)
    baseObject.endDate = Yup.date()
      .typeError(<Trans>Must be valid date</Trans>)
      // .min(moment.utc(), notAllowedTrans)
      .required(requiredTrans)
      .label(<Trans>End Date</Trans>)
  }
  return Yup.array()
    .ensure()
    .compact(function (v, index) {
      return !v.isDisplayed
    })
    .of(Yup.object(baseObject))
    .checkValidDates(validDatesTrans, datesRequired)
    .checkStartDateMinDate({
      phases: item.typeProps.phases,
      data,
      formikRef
    })
    // .checkChronology(chronologicalTrans)
    .label(<Trans>Milestones</Trans>)
}

export const FormMilestonesExtractKey = ({
  saveMap,
  value,
  item,
  connectedObjectId
}) => {
  const { phases = [] } = item.typeProps
  const inner = value.map((item, index) => {
    const shouldSaveValues = Boolean(phases[index])
    if (!shouldSaveValues) {
      return {
        FGM_Base__Due_Date__c: null,
        FGM_Base__Completion_Date__c: null,
        Primary_activities__c: '',
        FGM_Base__Description__c: '',
        Id: item.id
      }
    }

    return {
      FGM_Base__Due_Date__c: item.startDate,
      FGM_Base__Completion_Date__c: item.endDate,
      Primary_activities__c: item.primaryActivities,
      FGM_Base__Description__c: item.comments,
      FGM_Base__Status__c: phases[index].apiValue,
      Id: item.id
    }
  })
  saveMap[connectedObjectId].FGM_Base__Benchmarks__r = inner
}

const useStyles = makeStyles(styles)

export const formMilestonesinitialTouchedIds = ({ id, typeProps = {} }) => {
  const toRet = []
  const { phases = [] } = typeProps
  phases.forEach((phase, index) => {
    toRet.push(id + `[${index}].startDate`)
    toRet.push(id + `[${index}].endDate`)
  })
  return toRet
}

export const getFirstPossibleEndDate = ({ endDateMinDate, startDateMinDate }) => {
  const firstPossibleDay = moment.utc(startDateMinDate).add(1, 'days').format(dateFormat)
  let formattedEndMinDate = ''
  if (moment.isMoment(endDateMinDate)) {
    formattedEndMinDate = endDateMinDate.format(dateFormat)
  } else if (typeof endDateMinDate === 'string') {
    formattedEndMinDate = moment(endDateMinDate, dateFormat).format(dateFormat)
  } else {
    formattedEndMinDate = null
  }
  if (!formattedEndMinDate) {
    return firstPossibleDay
  } else {
    return formattedEndMinDate < firstPossibleDay
      ? firstPossibleDay
      : formattedEndMinDate
  }
}

/**
 * Form element which, for each project phase configured, renders an input that allows providing start date, end date, activities description and additional comments for each project phase. Milestones are saved as
 * FGM_Base__Benchmark__c child objects on connected Opportunity object.
 * @category Form
 * @subcategory Form elements
 * @component
 * @returns {JSX.Element}
 * @param  {Object} typeProps - Element specific properties that can be configured in form editor.
 * @param  {SalesforceObjectConnection}  typeProps.connectedObject Connected Salesforce Opportunity object.
 * @param  {Object[]} typeProps.phases Array of phases defined in the form editor. For each one user will need to fill the required information.
 * @param  {number}  [typeProps.commentsMaxChar] If provided, the number of characters that the comments text input field will be restricted to.
 * @param  {number}  [typeProps.commentsMaxWords] If provided, the number of words that the comments text input field will be restricted to.
 * @param  {number}  [typeProps.commentsRows=1] Number of rows which comments text input field should occupy.
 * @param  {boolean} [typeProps.commentsExpandable=false] If comments text field should expand further below if new line is inserted.
 * @param  {number}  [typeProps.activitiesMaxChar] If provided, the number of characters that the activities text input field will be restricted to.
 * @param  {number}  [typeProps.activitiesMaxWords] If provided, the number of words that the activities text input field will be restricted to.
 * @param  {number}  [typeProps.activitiesRows=1] Number of rows which activities text input field should occupy.
 * @param  {boolean} [typeProps.activitiesExpandable=false] If activities text field should expand further below if new line is inserted.
 */
export const FormMilestones = ({
  id,
  useMultiuser,
  muBag,
  title,
  disabled,
  connectedObject,
  editMode,
  langVersion,
  helpText,
  typeProps,
  i18n,
  ...props
}) => {
  const value = props.value || []
  const {
    phases = [],
    commentsMaxChar,
    commentsMaxWords,
    activitiesMaxChar,
    activitiesMaxWords,
    commentsRequired,
    activitiesRequired,
    datesRequired,
    commentsExpandable,
    commentsRows,
    activitiesRows,
    activitiesExpandable
  } = typeProps
  const { values, setFieldValue, errors } = useFormikContext()
  const tree = useSelector((state) => state.formEditorTree)
  const classes = useStyles()
  const invalid = Boolean(
    !connectedObject ||
      !connectedObject.Id ||
      connectedObject.attributes.type !== 'Opportunity'
  )

  const [toUpdate, setToUpdate] = useState(null)

  useEffect(() => {
    if (toUpdate) {
      const { field, idx, id } = toUpdate
      const error = errors[id]?.[idx]?.[field]
      const toSet = [...values[id]]
      const arrToUpd = []
      if (!error) {
        const updFunction = field === 'startDate' ? startDateChange : endDateChange
        updFunction({
          phases: toSet,
          id,
          idx,
          arrToUpd,
          fields: {
            startDate: 'startDate',
            endDate: 'endDate',
            endDateMinDate: 'endDateMinDate',
            endDateMaxDate: 'endDateMaxDate',
            startDateMinDate: 'startDateMinDate',
            startDateMaxDate: 'startDateMaxDate'
          }
        })
        setFieldValue(id, toSet)
        if (useMultiuser) {
          commitChangeToMultipleFields({
            array: arrToUpd,
            onSuccess: (response) => {},
            onFail: (error) => {
              console.log('error', { error })
            },
            token: muBag.token
          })
        }
        setToUpdate(null)
      }
    }
  }, [toUpdate, values, errors])

  // This effect is used to calculate all the min and max dates for start and end date of the phases
  useEffect(() => {
    if (!values?.[id]?.[0]?.init) {
      if (values[id][0]) {
        // set initial value to true to understand when to recalculate minMaxDays
        // because there is no this value after saving and reloading the form but we need to recalculate minMaxDays
        values[id][0].init = true
      }
      const _minMaxDays = getMinMaxDates({
        id,
        value,
        values,
        phasesProps: phases,
        typeProps
      })
      const minMaxDays = completeMinMaxDates(_minMaxDays)
      for (let i = 0; i < phases.length; i++) {
        if (
          (!minMaxDays[i].startDate || !minMaxDays[i].endDate) &&
          phases[i] &&
          i > 0
        ) {
          minMaxDays[i] = {
            ...minMaxDays[i],
            startDate:
              minMaxDays[i].startDate ||
              moment(minMaxDays[i - 1].endDate)
                .add(1, 'day')
                .format(dateFormat),
            endDate:
              minMaxDays[i].endDate ||
              moment(minMaxDays[i - 1].endDate)
                .add(8, 'day')
                .format(dateFormat)
          }
        }
      }
      setFieldValue(id, minMaxDays)
    }
  }, [values?.[id]?.[0]?.init])

  if (invalid && !editMode) {
    return (
      <div style={{ padding: 10, color: 'red' }}>
        <Trans>
          There is no object connected in editor or connected object is not of
          "Opportunity" type!
        </Trans>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <h3>{title}</h3>
      <FormHelptext
        helpText={helpText}
      />
      <div className='w-100 overflow-auto' style={{ padding: 20 }}>
        <TableContainer component={Paper}>
          <Table style={{ whiteSpace: 'pre' }}>
            <TableHead>
              <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                <TableCell style={{ paddingLeft: 50 }}>
                  <p className={classes.tHeaderText}>
                    <Trans>
                      Name
                    </Trans>
                  </p>
                </TableCell>
                <TableCell style={{ paddingLeft: 35 }}>
                  <p className={classes.tHeaderText}>
                    <Trans>
                      Start Date
                    </Trans>
                  </p>
                </TableCell>
                <TableCell style={{ paddingRight: 35 }}>
                  <p className={classes.tHeaderText}>
                    <Trans>
                      End Date
                    </Trans>
                  </p>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {phases.map((item, index) => {
                const {
                  startDateTooltip,
                  endDateTooltip,
                  startDateDaysAfterCurrentDate,
                  startDateMinDate,
                  startDateMaxDate,
                  endDateMinDate,
                  endDateMaxDate,
                  startDateMinDateElement,
                  startDateMaxDateElement,
                  endDateMinDateElement,
                  endDateMaxDateElement
                } = item

                const comments = value[index]?.comments || ''
                const activities = value[index]?.primaryActivities || ''
                // const error = moment(startDate).isAfter(moment(endDate)) && (
                //   <Trans>Start date cannot be set after end date</Trans>
                // )
                const startTooltip = parseFormLabelText({
                  text: startDateTooltip,
                  langVersion,
                  i18n
                })
                const endTooltip = parseFormLabelText({
                  text: endDateTooltip,
                  langVersion,
                  i18n
                })

                const commentsHelperArray = []
                if (commentsMaxWords) {
                  commentsHelperArray.push(
                    <span key='maxWords'>
                      {getCurrentWords(comments) + '/' + commentsMaxWords}{' '}
                      <Trans>words</Trans>
                      {'. '}
                    </span>
                  )
                }
                if (Number(commentsMaxChar)) {
                  commentsHelperArray.push(
                    <span key='maxCharacters'>
                      {countHelperText(comments, Number(commentsMaxChar))}
                      {'. '}
                    </span>
                  )
                }
                if (commentsRequired) {
                  commentsHelperArray.push(
                    <span key='commentsRequired'>
                      <Trans>Required</Trans>
                      {'. '}
                    </span>
                  )
                }

                const activitiesHelperArray = []
                if (activitiesMaxWords) {
                  activitiesHelperArray.push(
                    <span key='activitiesMaxWords'>
                      {getCurrentWords(activities) + '/' + activitiesMaxWords}{' '}
                      <Trans>words</Trans>
                      {'. '}
                    </span>
                  )
                }
                if (Number(activitiesMaxChar)) {
                  activitiesHelperArray.push(
                    <span key='activitiesMaxChar'>
                      {countHelperText(activities, Number(activitiesMaxChar))}
                      {'. '}
                    </span>
                  )
                }
                if (activitiesRequired) {
                  activitiesHelperArray.push(
                    <span key='activitiesRequired'>
                      <Trans>Required</Trans>
                      {'. '}
                    </span>
                  )
                }

                const activitiesId = id + `[${index}].primaryActivities`
                const commentsId = id + `[${index}].comments`

                const disableIfStartDateElementNull = shouldDatePickerBeDisable(
                  values,
                  typeProps = {
                    minDateElement: startDateMinDateElement,
                    maxDateElement: startDateMaxDateElement
                  }
                )
                const disableIfEndDateElementNull = shouldDatePickerBeDisable(
                  values,
                  typeProps = {
                    minDateElement: endDateMinDateElement,
                    maxDateElement: endDateMaxDateElement
                  }
                )
                const {
                  startMinMessage,
                  startMaxMessage,
                  startDateMinDate: startMinDate,
                  startDateMaxDate: startMaxDate,
                  endMinMessage,
                  endMaxMessage,
                  endDateMinDate: endMinDate,
                  endDateMaxDate: endMaxDate
                } = getMilestoneDateLimits({
                  values,
                  data: tree,
                  startDateMinDate,
                  startDateMaxDate,
                  startDateDaysAfterCurrentDate,
                  startDateMinDateElement,
                  startDateMaxDateElement,
                  endDateMinDate,
                  endDateMaxDate,
                  endDateMinDateElement,
                  endDateMaxDateElement
                })

                return [
                  <TableRow
                    key={index}
                    style={{ borderBottom: 'solid 0px #ffffff' }}
                  >
                    <TableCell style={{ paddingLeft: 50 }}>
                      <FormTopLabel>
                        {parseFormLabelText({
                          text: item.label,
                          langVersion,
                          i18n
                        })}
                      </FormTopLabel>
                    </TableCell>
                    <TableCell style={{ width: '40%', verticalAlign: 'top', paddingLeft: 35 }}>
                      <Grid container wrap='nowrap'>
                        <MUDatePicker
                          id={id + `[${index}].startDate`}
                          muBag={muBag}
                          useMultiuser={useMultiuser}
                          displayFieldHistoryIcon
                          autoOk
                          fullWidth
                          minDate={startMinDate}
                          maxDate={startMaxDate}
                          style={{
                            width: '100%',
                            margin: 0,
                            paddingRight: 0,
                            paddingTop: '15px',
                            paddingLeft: '15px'
                          }}
                          disabled={disabled || disableIfStartDateElementNull}
                          format={dateFormat}
                          calendarStyles={calendarStyles}
                          shouldUnlockFieldWithoutChangesAfterOnChangeEvent
                          /** property shouldUnlockFieldWithoutChangesAfterOnChangeEvent shows if field should be unlocked without changes
                           * after onChange event. */
                          onChange={(event) => {
                            const toSet = [...values[id]]
                            const item = { ...toSet[index] }
                            /** if date is not changed, unlock field without changes */
                            if (
                              moment(event).format(dateFormat) ===
                              moment(item.startDate).format(dateFormat)
                            ) {
                              return
                            }
                            /** Create array of fields to be changed by this event.
                             * All this fields will be handled by commitChangeToMultipleFields to save them in backend history properly */
                            if (!toSet[index]) {
                              toSet[index] = {}
                            }
                            toSet[index].startDate = event
                            setTimeout(() => {
                              setToUpdate({
                                phases: toSet,
                                id,
                                idx: index,
                                field: 'startDate'
                              })
                            }, 100)
                          }}
                          helperText={disableIfStartDateElementNull
                            ? disabledDatePickerHelperText
                            : (
                              <>
                                {startMinMessage && <div>{startMinMessage}</div>}
                                {startMaxMessage && <div>{startMaxMessage}</div>}
                              </>
                              )}
                        />
                        {startTooltip && (
                          <Grid item sx={{ paddingTop: '24px' }}>
                            <TooltipLabelIcon tooltip={startTooltip} />
                          </Grid>
                        )}
                      </Grid>
                    </TableCell>
                    <TableCell style={{ width: '40%', verticalAlign: 'top', paddingRight: 35 }}>
                      <Grid container wrap='nowrap'>
                        <MUDatePicker
                          id={id + `[${index}].endDate`}
                          muBag={muBag}
                          useMultiuser={useMultiuser}
                          displayFieldHistoryIcon
                          autoOk
                          fullWidth
                          minDate={endMinDate}
                          maxDate={endMaxDate}
                          style={{
                            width: '100%',
                            margin: 0,
                            paddingRight: 0,
                            paddingTop: 15,
                            paddingLeft: 15
                          }}
                          calendarStyles={calendarStyles}
                          disabled={disabled || disableIfEndDateElementNull}
                          format={dateFormat}
                          /** property shouldUnlockFieldWithoutChangesAfterOnChangeEvent shows if field should be unlocked without changes
                           * after onChange event. */
                          shouldUnlockFieldWithoutChangesAfterOnChangeEvent
                          onChange={(event) => {
                            const toSet = [...values[id]]
                            const item = { ...toSet[index] }
                            /** if date is not changed, unlock field without changes */
                            if (
                              moment(event).format(dateFormat) ===
                              moment(item.endDate).format(dateFormat)
                            ) {
                              return
                            }
                            /** Create array of fields to be changed by this event.
                             * All this fields will be handled by commitChangeToMultipleFields to save them in backend history properly */
                            if (!toSet[index]) {
                              toSet[index] = {}
                            }
                            toSet[index].endDate = event
                            setTimeout(() => {
                              setToUpdate({
                                phases: toSet,
                                id,
                                idx: index,
                                field: 'endDate'
                              })
                            }, 100)
                          }}
                          helperText={disableIfEndDateElementNull
                            ? disabledDatePickerHelperText
                            : (
                              <>
                                {endMinMessage && <div>{endMinMessage}</div>}
                                {endMaxMessage && <div>{endMaxMessage}</div>}
                              </>
                              )}
                        />
                        {endTooltip && (
                          <Grid item sx={{ paddingTop: '24px' }}>
                            <TooltipLabelIcon tooltip={endTooltip} />
                          </Grid>
                        )}
                      </Grid>
                    </TableCell>
                  </TableRow>,
                  <TableRow key={index + phases.length} className={classes.tr}>
                    <TableCell colSpan={3} className={classes.tcSpan4}>
                      <FormTopLabel required={activitiesRequired}>
                        <Trans>Primary Activities</Trans>
                      </FormTopLabel>
                      <TextFieldWithFormik
                        name={activitiesId}
                        muBag={muBag}
                        useMultiuser={useMultiuser}
                        multiline
                        minRows={activitiesRows || 5}
                        maxRows={activitiesExpandable ? null : activitiesRows || 5}
                        margin='normal'
                        disabled={disabled}
                        maxWords={activitiesMaxWords && Number(activitiesMaxWords)}
                        limit={activitiesMaxChar && Number(activitiesMaxChar)}
                        required={activitiesRequired}
                      />
                    </TableCell>
                  </TableRow>,
                  <TableRow
                    key={index + phases.length * 2}
                    className={classes.tr}
                  >
                    <TableCell colSpan={3} className={classes.tcSpan4}>
                      <FormTopLabel required={commentsRequired}>
                        <Trans>Comments</Trans>
                      </FormTopLabel>
                      <TextFieldWithFormik
                        name={commentsId}
                        muBag={muBag}
                        useMultiuser={useMultiuser}
                        multiline
                        minRows={commentsRows || 5}
                        maxRows={
                              commentsExpandable ? null : commentsRows || 5
                            }
                        margin='normal'
                        disabled={disabled}
                        maxWords={
                              commentsMaxWords && Number(commentsMaxWords)
                            }
                        limit={commentsMaxChar && Number(commentsMaxChar)}
                        required={commentsRequired}
                      />
                    </TableCell>
                  </TableRow>
                ]
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  )
}

/** Milestone component for printing
 * @component
 * @returns {JSX.Element}
 * @param  {number} index - Index of the milestone.
 * @param  {string} phase - Phase of the milestone.
 * @param  {Object} milestone - Milestone object.
 */
export const Milestone = ({ index = 0, phase = null, milestone }) => {
  return (
    <div
      key={index}
      style={{ width: '100%', padding: 10 }}
    >
      <Typography
        style={{
          fontSize: 18,
          fonWeight: 'bold',
          textAlign: 'center',
          margin: 10
        }}
      >
        {phase
          ? (
            <Trans id={myI18n._(t`${phase.apiValue}`)} />
            )
          : (
              milestone.phase
            )}
      </Typography>
      <Grid container style={{ marginBottom: 15 }}>
        <CustomDatePicker
          label={<Trans>Start Date</Trans>}
          value={milestone.startDate}
          readOnly
        />
        <CustomDatePicker
          label={<Trans>End Date</Trans>}
          value={milestone.endDate}
          readOnly
        />
        {/* <Grid item xs style={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: '18px',
              fontWeight: 500,
              color: '#000000'
            }}
          >
            <Trans>End Date</Trans>
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: '18px',
              fontWeight: 500,
              color: '#000000'
            }}
          >
            {endDate}
          </Typography>
        </Grid> */}
      </Grid>
      <Divider />
      <div style={{ padding: 8 }}>
        <Typography className='form-print-subtitle'>
          <Trans>Primary Activities</Trans>
        </Typography>
        <Typography className='form-read-only-text'>{milestone.primaryActivities}</Typography>
      </div>
      <Divider />
      <div style={{ padding: 8 }}>
        <Typography className='form-print-subtitle'>
          <Trans>Comments</Trans>
        </Typography>
        <Typography className='form-read-only-text'>{milestone.comments}</Typography>
      </div>
    </div>
  )
}
